import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeamPermissionsContext = createContext({
  loading: true,
  isSubUser: false,
  isOwner: true,
  roleTemplate: null,
  permissions: [],
  canWriteAsset: () => true,
  canWritePage: () => true,
});

/**
 * Provides the sub-user's permission snapshot to the whole tree so write
 * actions (Add Entry, Edit, Upload Contract Note, etc.) can be hidden when
 * the user lacks `edit` / `transaction` on the relevant scope.
 *
 * Workspace owners always pass through (`canWriteAsset` / `canWritePage`
 * return `true` unconditionally). Only sub-users hit the per-scope check.
 *
 * Backend reference: ``GET /api/team/me`` returns
 *   { is_owner, role_template, permissions: [
 *       { scope_type: "portfolio_asset"|"page",
 *         scope_id:  "<pid>:equity" | "reports" | ...,
 *         view, edit, transaction }
 *   ] }
 */
export function TeamPermissionsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Only sub-users need the permission snapshot. Owners get unrestricted
    // access — skip the fetch to keep their first paint fast.
    if (!user) { setData(null); setLoading(false); return; }
    if (!user.is_sub_user) { setData({ is_owner: true, permissions: [] }); setLoading(false); return; }
    setLoading(true);
    axios.get(`${API_URL}/api/team/me`, { withCredentials: true })
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  const permissions = useMemo(() => (data && Array.isArray(data.permissions) ? data.permissions : []), [data]);
  const isOwner = !user?.is_sub_user;

  const findPerm = useCallback((scopeType, scopeId) => {
    return permissions.find(p => p.scope_type === scopeType && p.scope_id === scopeId);
  }, [permissions]);

  const canWriteAsset = useCallback((portfolioId, assetClass) => {
    if (isOwner) return true;
    if (!portfolioId || !assetClass) return false;
    const p = findPerm('portfolio_asset', `${portfolioId}:${assetClass}`);
    return !!(p && (p.transaction || p.edit));
  }, [isOwner, findPerm]);

  const canViewAsset = useCallback((portfolioId, assetClass) => {
    if (isOwner) return true;
    if (!portfolioId || !assetClass) return false;
    const p = findPerm('portfolio_asset', `${portfolioId}:${assetClass}`);
    return !!(p && (p.view || p.edit || p.transaction));
  }, [isOwner, findPerm]);

  const canWritePage = useCallback((page) => {
    if (isOwner) return true;
    if (!page) return false;
    const p = findPerm('page', page);
    return !!(p && (p.transaction || p.edit));
  }, [isOwner, findPerm]);

  const value = useMemo(() => ({
    loading,
    isSubUser: !!user?.is_sub_user,
    isOwner,
    roleTemplate: data?.role_template || null,
    permissions,
    canWriteAsset,
    canViewAsset,
    canWritePage,
  }), [loading, user, isOwner, data, permissions, canWriteAsset, canViewAsset, canWritePage]);

  return (
    <TeamPermissionsContext.Provider value={value}>
      {children}
    </TeamPermissionsContext.Provider>
  );
}

export function useTeamPermissions() {
  return useContext(TeamPermissionsContext);
}
